import {
  Box,
  Container,
  CircularProgress,
  Typography,
  Card,
  CardContent,
} from "@mui/material";
import { CheckCircle, Cancel } from "@mui/icons-material";
import ScrollToTop from "../nav/ScrollToTop";
import { usePayment } from "../../context/PaymentProvider";
import ActionButton from "../account/ActionButton";
const PaymentConfirmation = ({
  amount,
  accountReference,
  handleComplete,
}: {
  amount: number;
  accountReference: string;
  handleComplete: () => void;
}) => {
  const { paymentId, usePollPaymentStatus } = usePayment();
  const { paymentStatus, error, isLoading, isPending } =
    usePollPaymentStatus(paymentId);
  const paymentSuccess =
    paymentStatus?.status === "AUTHORISED" ||
    paymentStatus?.status === "PUSHED" ||
    paymentStatus?.status === "COMPLETE";

  return (
    <PaymentConfirmationDisplay
      amount={amount}
      accountReference={accountReference}
      error={error}
      isLoading={isLoading}
      isPending={isPending}
      paymentSuccess={paymentSuccess}
      onComplete={handleComplete}
    />
  );
};

export const PaymentConfirmationDisplay = ({
  amount,
  accountReference,
  error,
  isLoading,
  isPending,
  onComplete,
  paymentSuccess,
  paymentId,
  onTryAgain,
}: {
  accountReference?: string;
  amount: string | number;
  error?: Error;
  isLoading: boolean;
  isPending: boolean;
  paymentSuccess?: boolean;
  paymentId?: string;
  onComplete: () => void;
  onTryAgain?: () => void;
}) => {
  if (error) return <Typography>Payment Error</Typography>;

  if (isLoading || isPending || paymentSuccess === undefined) {
    return (
      <Box
        sx={{
          bgcolor: "background.paper",
          pt: 8,
          pb: 6,
          textAlign: "center",
        }}>
        <Container maxWidth='sm'>
          <CircularProgress color='primary' size='4rem' />
        </Container>
      </Box>
    );
  }

  if (error) return <Typography>Payment Error</Typography>;

  if (isLoading) {
    return (
      <Box
        sx={{
          bgcolor: "background.paper",
          pt: 8,
          pb: 6,
          textAlign: "center",
        }}>
        <Container maxWidth='sm'>
          <CircularProgress color='primary' size='4rem' />
        </Container>
      </Box>
    );
  }

  function createData(key: string, value: string | number | undefined) {
    return { key, value };
  }

  const tabularData = [
    createData("Transaction date", new Date().toLocaleDateString()),
  ];
  if (paymentId) {
    tabularData.push(createData("Transaction number", paymentId));
  }
  if (accountReference) {
    tabularData.push(createData("Account reference", accountReference));
  }

  const formattedAmount = new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(amount));

  const payment = paymentSuccess
    ? {
        success: true,
        icon: <CheckCircle color='success' sx={{ fontSize: 72, mb: 2 }} />,
        title: "Payment Complete",
        subText: `Thank you for your payment of ${formattedAmount}`,
        tabularData,
      }
    : {
        success: false,
        icon: <Cancel color='error' sx={{ fontSize: 72, mb: 2 }} />,
        title: "Payment Failed",
        subText:
          "There was a problem processing your payment. Please contact customer support.",
        tabularData,
      };

  return (
    <>
      <ScrollToTop />
      <Box
        sx={{
          mx: "auto",
          p: 3,
          textAlign: "center",
          width: { xs: "100%", md: "65%" },
        }}>
        {payment.icon}
        <Typography
          variant='h5'
          color='text.primary'
          fontWeight='bold'
          paragraph>
          {payment.title}
        </Typography>
        <Typography paddingBottom={2}>{payment.subText}</Typography>

        {payment.tabularData.map((row) => (
          <Card
            key={row.key}
            sx={{
              mb: 2,
            }}>
            <CardContent>
              <Typography
                textAlign='left'
                width='35%'
                sx={{ display: "inline-block" }}>
                {row.key}
              </Typography>
              <Box
                textAlign='right'
                width='65%'
                fontWeight='bold'
                sx={{
                  display: "inline-block",
                }}>
                {row.value}
              </Box>
            </CardContent>
          </Card>
        ))}
        {!paymentSuccess && onTryAgain !== undefined ? (
          <ActionButton
            id='try-again-btn'
            borderColor='primary.dark'
            buttonColor='transparent'
            buttonTextColor='primary.dark'
            buttonTitle='Try Again'
            variant='outlined'
            hoverColor='primary.dark'
            hoverBorderColor={"primary.dark"}
            hoverTextColor='secondary.contrastText'
            onClick={onTryAgain}
          />
        ) : null}
        <ActionButton
          id='continue-btn'
          variant='contained'
          buttonColor='secondary.main'
          buttonTextColor='primary.contrastText'
          borderColor='transparent'
          hoverColor='primary.contrastText'
          hoverBorderColor='secondary.main'
          hoverTextColor='secondary.main'
          buttonTitle='Return Home'
          onClick={onComplete}
        />
      </Box>
    </>
  );
};

export default PaymentConfirmation;
